import type { RealtimePostgresChangesPayload } from "@supabase/supabase-js";

import { useAtom } from "jotai";
import { useCallback, useEffect, useMemo, useRef } from "react";

import type { EventGuest } from "@/lib/event-queries";

import { eventMembersFamily } from "@/atoms/event-members";
import {
  fetchEventMemberDetails,
  fetchSingleEventMember,
} from "@/lib/event-queries";
import { supabase } from "@/lib/supabase";

interface EventMemberRow {
  created_at: string;
  event_id: string;
  event_ticket_kind_id: null | string;
  invite_source: null | string;
  referred_by_user_id: null | string;
  role: string;
  rsvp_status: string;
  stripe_payment_intent_id: null | string;
  stripe_payment_intent_status: null | string;
  user_id: string;
}

export function useEventMembers(eventId: string, userId: null | string) {
  const [members, setMembers] = useAtom(eventMembersFamily(eventId));
  const mountedRef = useRef(true);

  const handleChange = useCallback(
    async (payload: RealtimePostgresChangesPayload<EventMemberRow>) => {
      console.log("Received event member change", payload);
      if (!mountedRef.current) return;

      if (payload.eventType === "DELETE") {
        const deleted = payload.old as Partial<EventMemberRow>;
        if (!deleted.user_id) return;
        setMembers((prev) => prev.filter((m) => m.user_id !== deleted.user_id));
        return;
      }

      const row = payload.new as EventMemberRow;
      if (!row.user_id) return;

      const detail = await fetchSingleEventMember(eventId, row.user_id);
      if (!detail || !mountedRef.current) return;

      setMembers((prev) =>
        prev.some((m) => m.user_id === detail.user_id)
          ? prev.map((m) => (m.user_id === detail.user_id ? detail : m))
          : [...prev, detail],
      );
    },
    [eventId, setMembers],
  );

  useEffect(() => {
    mountedRef.current = true;

    console.log(
      "Setting up event members subscription for eventId",
      eventId,
      "and userId",
      userId,
    );
    if (!userId || !eventId) {
      setMembers([]);
      return;
    }

    fetchEventMemberDetails(eventId).then((result) => {
      if (!mountedRef.current) return;
      setMembers(result);
    });

    const channel = supabase
      .channel(`event-members-${eventId}`)
      .on<EventMemberRow>(
        "postgres_changes",
        {
          event: "*",
          filter: `event_id=eq.${eventId}`,
          schema: "public",
          table: "event_member",
        },
        handleChange,
      )
      .subscribe();

    return () => {
      mountedRef.current = false;
      supabase.removeChannel(channel);
    };
  }, [eventId, userId, handleChange, setMembers]);

  const guests = useMemo<EventGuest[]>(
    () =>
      members
        .filter((m) => m.rsvp_status === "confirmed" && m.name)
        .map((m) => ({
          isMutual: m.is_contact ?? false,
          isSelf: m.user_id === userId,
          name: m.name ?? "guest",
          role: m.role,
          user_id: m.user_id,
        })),
    [members, userId],
  );

  const hosts = useMemo(
    () =>
      members
        .filter((m) => m.role === "admin" && m.name)
        .map((m) => (m.name ?? "Host").split(" ")[0].toLowerCase()),
    [members],
  );

  const myRsvp = useMemo(
    () => members.find((m) => m.user_id === userId) ?? null,
    [members, userId],
  );

  const refreshMember = useCallback(
    async (uid: string) => {
      const detail = await fetchSingleEventMember(eventId, uid);
      if (!detail) return;
      setMembers((prev) =>
        prev.some((m) => m.user_id === detail.user_id)
          ? prev.map((m) => (m.user_id === detail.user_id ? detail : m))
          : [...prev, detail],
      );
    },
    [eventId, setMembers],
  );

  return { guests, hosts, myRsvp, refreshMember };
}
